<template>
    <div class="col-md-9 panel panel-default"></div>
</template>

<script>
import * as d3 from 'd3'

export default {
  name: 'sankey',
  props: ['data', 'title'],
  data: () => {
    return {
      maximumRows: 25,
      chartWidth: window.innerWidth,
      chartHeight: window.innerHeight
    }
  },

  mounted () {
    this.loadSankeyDiagram()
  },

  methods: {
    loadSankeyDiagram () {
      let graph = this.data.data
      if (graph && graph.nodes.length > 0 && graph.links.length > 0) {
        this.renderSankey(graph)
      } else {
        $(this.$el).html('No Data Found!')
      }
    },

    renderSankey (graph) {
      let maxNumberBeforeOther = this.maximumRows * 0.5
      let maxNumberBeforeOtherMax = 20
      if (maxNumberBeforeOther > maxNumberBeforeOtherMax) {
        maxNumberBeforeOther = maxNumberBeforeOtherMax
      }
      this.createRHSOtherCategory(graph, maxNumberBeforeOther, false)
      this.setUpNodesBasedOnLinks(graph)
      this.drawSankey(graph)
    },

    createRHSOtherCategory (graph, maxNumberBeforeOther, isOnlyShowOther) {
      function get (targets, needle) {
        for (let idx in targets) {
          let target = targets[idx]
          if (target.name === needle) {
            return target
          }
        }
        return null
      }

      function getIndex (needle, targets) {
        for (let idx in targets) {
          let target = targets[idx]
          if (target.name === needle) {
            return idx
          }
        }
      }

      let links = graph.links
      let targets = []
      for (let idx in links) {
        let link = links[idx]
        let target = link.target
        let targetObject = get(targets, target)
        if (targetObject === null) {
          targetObject = {name: target, value: 0}
          targets.push(targetObject)
        }
        targetObject.value += parseInt(link.value)
      }
      targets.sort(function (a, b) { return b.value - a.value })

      for (let idx in links) {
        let link = links[idx]
        let target = link.target
        let indexOfTargetInSortedArray = getIndex(target, targets)
        if (indexOfTargetInSortedArray >= maxNumberBeforeOther) {
          if (!isOnlyShowOther) {
            link.target = 'Other'
          }
        } else {
          if (isOnlyShowOther) {
            link.target = 'Other'
          }
        }
      }

      let newLinks = []
      let hashtable = {}
      for (let idx in links) {
        let link = links[idx]
        let target = link.target
        let source = link.source
        let skip = false
        if (target === 'Other') {
          let previous = hashtable[source]
          if (isOnlyShowOther) {
            skip = true
          } else {
            if (!previous) {
              hashtable[source] = link
            } else {
              previous.value = parseInt(previous.value)
              skip = true
            }
          }
        }
        if (!skip) {
          newLinks.push(link)
        }
      }
      graph.links = newLinks
    },

    setUpNodesBasedOnLinks (data) {
      let links = data.links
      let nodes = []
      for (let idx in links) {
        let link = links[idx]
        if (nodes.indexOf(link.source) < 0) {
          nodes.push(link.source)
        }
        if (nodes.indexOf(link.target) < 0) {
          nodes.push(link.target)
        }
      }
      let nodeList = []
      for (let idx in nodes) {
        let node = nodes[idx]
        let nodeObj = {name: node}
        nodeList.push(nodeObj)
      }
      data.nodes = nodeList
    },

    drawSankey (graph) {
      $(this.$el).empty()
      let units = 'Students'

      let widthOfContainer = this.chartWidth
      let heightOfContainer = this.chartHeight
      let margin = {top: 10, right: 20, bottom: 10, left: 10}
      let width = widthOfContainer - margin.left - margin.right - 10
      let height = heightOfContainer - margin.top - margin.bottom

      let formatNumber = d3.format(',.0f')
      let format = function (d) { return formatNumber(d) + ' ' + units }
      let color = d3.scaleOrdinal().domain(graph.links.map(function (x) { return x.source })).range(d3.schemeSet1)

      let chart = d3.select(this.$el)
      let svg = chart.append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

      let sankey = this.sankey()
        .nodeWidth(20)
        .nodePadding(10)
        .size([width, height])

      let path = sankey.link()

      let nodeMap = {}
      graph.nodes.forEach(function (x) { nodeMap[x.name] = x })
      graph.links = graph.links.map(function (x) {
        return {
          source: nodeMap[x.source],
          target: nodeMap[x.target],
          value: x.value
        }
      })

      sankey
        .nodes(graph.nodes)
        .links(graph.links)
        .layout(32)

      let link = svg.append('g').selectAll('.link')
        .data(graph.links)
        .enter().append('path')
        .attr('class', 'link')
        .attr('d', path)
        .style('stroke-width', d => Math.max(1, d.dy))
        .style('stroke', function (d) {
          d.color = color(d.source.name.replace(/ .*/, ''))
          return d.color
        })
        .style('fill', 'none')
        .style('stroke-opacity', 0.3)
        .sort((a, b) => b.dy - a.dy)
        .on('mouseover', function () {
          d3.select(this).style('stroke-opacity', 0.6)
        })
        .on('mouseout', function () {
          d3.select(this).style('stroke-opacity', 0.3)
        })

      link.append('title')
        .text(function (d) { return d.source.name + ' → ' + d.target.name + '\n' + format(d.value) })

      let node = svg.append('g').selectAll('.node')
        .data(graph.nodes)
        .enter().append('g')
        .attr('class', 'node')
        .attr('transform', d => `translate(${d.x},${d.y})`)
        .call(d3.drag()
          .subject(d => d)
          .on('start', function () { this.parentNode.appendChild(this) })
          .on('drag', dragmove))

      node.append('rect')
        .attr('height', function (d) { return d.dy })
        .attr('width', sankey.nodeWidth())
        .style('fill', function (d) {
          d.color = color(d.name.replace(/ .*/, ''))
          return d.color
        })
        .style('stroke', function (d) { return d3.rgb(d.color).darker(2) })
        .on('click', (d) => {
          this.resizeSankey(d.name)
        })
        .append('title')
        .text(function (d) { return d.name + '\n' + format(d.value) })

      node.append('text')
        .attr('x', -5)
        .attr('y', function (d) { return d.dy / 2 })
        .attr('dy', '.15em')
        .attr('text-anchor', 'end')
        .attr('transform', null)
        .text(function (d) { return d.name })
        .filter(function (d) { return d.x < width / 2 })
        .attr('x', 5 + sankey.nodeWidth())
        .attr('text-anchor', 'start')

      function dragmove (d) {
        d3.select(this).attr('transform',
          'translate(' + (
            d.x = Math.max(0, Math.min(width - d.dx, d3.event.x))
          ) + ',' + (
            d.y = Math.max(0, Math.min(height - d.dy, d3.event.y))
          ) + ')'
        )
        sankey.relayout()
        link.attr('d', path)
      }
    },

    resizeSankey (name) {
      if (this.data) {
        let data = this.data.data
        if (name) {
          this.removeLinksThatStartWith(data, name)
        }
        this.renderSankey(data)
      }
    },

    removeLinksThatStartWith (data, concentration) {
      var links = data.links
      var filteredLinks = []
      for (var idx in links) {
        var link = links[idx]
        if (link.source === concentration) {
          filteredLinks.push(link)
        }
      }
      data.links = filteredLinks
      this.setUpNodesBasedOnLinks(data)
    },

    sankey () {
      let sankey = {}
      let nodeWidth = 20
      let nodePadding = 10
      let size = [1, 1]
      let nodes = []
      let links = []

      sankey.nodeWidth = function (_) {
        if (!arguments.length) {
          return nodeWidth
        }
        nodeWidth = +_
        return sankey
      }

      sankey.nodePadding = function (_) {
        if (!arguments.length) {
          return nodePadding
        }
        nodePadding = +_
        return sankey
      }

      sankey.nodes = function (_) {
        if (!arguments.length) {
          return nodes
        }
        nodes = _
        return sankey
      }

      sankey.links = function (_) {
        if (!arguments.length) {
          return links
        }
        links = _
        return sankey
      }

      sankey.size = function (_) {
        if (!arguments.length) {
          return size
        }
        size = _
        return sankey
      }

      sankey.layout = function (iterations) {
        computeNodeLinks()
        computeNodeValues()
        computeNodeBreadths()
        computeNodeDepths(iterations)
        computeLinkDepths()
        return sankey
      }

      sankey.relayout = function () {
        computeLinkDepths()
        return sankey
      }

      sankey.link = function () {
        let curvature = 0.5

        function link (d) {
          let x0 = d.source.x + d.source.dx
          let x1 = d.target.x
          let xi = d3.interpolateNumber(x0, x1)
          let x2 = xi(curvature)
          let x3 = xi(1 - curvature)
          let y0 = d.source.y + d.sy + d.dy / 2
          let y1 = d.target.y + d.ty + d.dy / 2
          return 'M' + x0 + ',' + y0 + 'C' + x2 + ',' + y0 + ' ' + x3 + ',' + y1 + ' ' + x1 + ',' + y1
        }

        link.curvature = function (_) {
          if (!arguments.length) {
            return curvature
          }
          curvature = +_
          return link
        }

        return link
      }

      function computeNodeLinks () {
        nodes.forEach(function (node) {
          node.sourceLinks = []
          node.targetLinks = []
        })
        links.forEach(function (link) {
          let source = link.source
          let target = link.target
          if (typeof source === 'number') {
            source = link.source = nodes[link.source]
          }
          if (typeof target === 'number') {
            target = link.target = nodes[link.target]
          }
          source.sourceLinks.push(link)
          target.targetLinks.push(link)
        })
      }

      function computeNodeValues () {
        nodes.forEach(function (node) {
          node.value = Math.max(
            d3.sum(node.sourceLinks, value),
            d3.sum(node.targetLinks, value)
          )
        })
      }

      function computeNodeBreadths () {
        let remainingNodes = nodes
        let nextNodes
        let x = 0

        while (remainingNodes.length) {
          nextNodes = []
          remainingNodes.forEach(function (node) {
            node.x = x
            node.dx = nodeWidth
            node.sourceLinks.forEach(function (link) {
              nextNodes.push(link.target)
            })
          })
          remainingNodes = nextNodes
          ++x
        }

        moveSinksRight(x)
        scaleNodeBreadths((size[0] - nodeWidth) / (x - 1))
      }

      /*
      function moveSourcesRight () {
        nodes.forEach(function (node) {
          if (!node.targetLinks.length) {
            node.x = d3.min(node.sourceLinks, function (d) { return d.target.x }) - 1
          }
        })
      }
      */

      function moveSinksRight (x) {
        nodes.forEach(function (node) {
          if (!node.sourceLinks.length) {
            node.x = x - 1
          }
        })
      }

      function scaleNodeBreadths (kx) {
        nodes.forEach(function (node) {
          node.x *= kx
        })
      }

      function computeNodeDepths (iterations) {
        let nodesByBreadth = d3.nest()
          .key(function (d) { return d.x })
          .sortKeys(d3.ascending)
          .entries(nodes)
          .map(function (d) { return d.values })

        initializeNodeDepth()
        resolveCollisions()
        for (let alpha = 1; iterations > 0; --iterations) {
          relaxRightToLeft(alpha *= 0.99)
          resolveCollisions()
          relaxLeftToRight(alpha)
          resolveCollisions()
        }

        function initializeNodeDepth () {
          let ky = d3.min(nodesByBreadth, function (nodes) {
            return (size[1] - (nodes.length - 1) * nodePadding) / d3.sum(nodes, value)
          })

          nodesByBreadth.forEach(function (nodes) {
            nodes.forEach(function (node, i) {
              node.y = i
              node.dy = node.value * ky
            })
          })

          links.forEach(function (link) {
            link.dy = link.value * ky
          })
        }

        function relaxLeftToRight (alpha) {
          nodesByBreadth.forEach(function (nodes, breadth) {
            nodes.forEach(function (node) {
              if (node.targetLinks.length) {
                let y = d3.sum(node.targetLinks, weightedSource) / d3.sum(node.targetLinks, value)
                node.y += (y - center(node)) * alpha
              }
            })
          })

          function weightedSource (link) {
            return center(link.source) * link.value
          }
        }

        function relaxRightToLeft (alpha) {
          nodesByBreadth.slice().reverse().forEach(function (nodes) {
            nodes.forEach(function (node) {
              if (node.sourceLinks.length) {
                let y = d3.sum(node.sourceLinks, weightedTarget) / d3.sum(node.sourceLinks, value)
                node.y += (y - center(node)) * alpha
              }
            })
          })

          function weightedTarget (link) {
            return center(link.target) * link.value
          }
        }

        function resolveCollisions () {
          nodesByBreadth.forEach(function (nodes) {
            let node
            let dy
            let y0 = 0
            let n = nodes.length
            let i

            nodes.sort(ascendingDepth)
            for (i = 0; i < n; ++i) {
              node = nodes[i]
              dy = y0 - node.y
              if (dy > 0) node.y += dy
              y0 = node.y + node.dy + nodePadding
            }

            dy = y0 - nodePadding - size[1]
            if (dy > 0) {
              y0 = node.y -= dy
              for (i = n - 2; i >= 0; --i) {
                node = nodes[i]
                dy = node.y + node.dy + nodePadding - y0
                if (dy > 0) node.y -= dy
                y0 = node.y
              }
            }
          })
        }

        function ascendingDepth (a, b) {
          return a.y - b.y
        }
      }

      function computeLinkDepths () {
        nodes.forEach(function (node) {
          node.sourceLinks.sort(ascendingTargetDepth)
          node.targetLinks.sort(ascendingSourceDepth)
        })
        nodes.forEach(function (node) {
          let sy = 0
          let ty = 0
          node.sourceLinks.forEach(function (link) {
            link.sy = sy
            sy += link.dy
          })
          node.targetLinks.forEach(function (link) {
            link.ty = ty
            ty += link.dy
          })
        })

        function ascendingSourceDepth (a, b) {
          return a.source.y - b.source.y
        }

        function ascendingTargetDepth (a, b) {
          return a.target.y - b.target.y
        }
      }

      function center (node) {
        return node.y + node.dy / 2
      }

      function value (link) {
        return link.value
      }

      return sankey
    }
  },

  computed: {
  },

  watch: {
  }
}
</script>
<style>
    .node rect {
        cursor: move;
        fill-opacity: .9;
        shape-rendering: crispEdges;
    }
</style>



// WEBPACK FOOTER //
// src/app/explore/components/Sankey.vue